import { forwardRef } from 'react';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

import { IconButton } from '../../buttons';
import { CampaignCardBadge } from './Badge';
import { Theme } from '@fagmobler/sanity';
import { processTitle } from '../../../utils/processTitle';

export type CampaignBadgeType =
  | 'promotion'
  | 'sale'
  | 'news'
  | 'blackweek'
  | 'blackweek-logo-light'
  | 'blackweek-logo-dark'
  | 'sale-red'
  | 'salg-logo'
  | 'customBrandingLogo'
  | 'sale-with-extra-bubble';
export type CampaignCardVariant = 'default' | 'overlay';
export type CampaignCardSize = 'sm' | 'md' | 'lg';

export type CampaignCardProps = {
  className?: string;
  badgeType?: CampaignBadgeType;
  badgeText?: string;
  badgePreText?: string;
  image?: React.ReactNode;
  centerBranding?: any;
  topLeftBranding?: any;
  includeTopBranding?: boolean;
  topBranding?: any;
  smallBadgeText?: string;
  imagePosition?: 'before' | 'after';
  subTitle?: string;
  title?: string;
  body?: string;
  href?: string;
  size?: CampaignCardSize;
  variant?: CampaignCardVariant;
  showButton?: boolean;
  fill?: boolean;
  theme?: Theme;
};

export const CampaignCard = forwardRef(function CampaignCard(
  {
    className = '',
    badgeType,
    badgeText,
    badgePreText,
    image,
    imagePosition = 'before',
    subTitle,
    title = '',
    body,
    showButton = true,
    size = 'lg',
    variant = 'default',
    fill,
    theme = 'neutralDark',
    centerBranding,
    topLeftBranding,
    includeTopBranding = false,
    topBranding,
    smallBadgeText,
    ...rest
  }: CampaignCardProps,
  ref
) {
  const small = size === 'sm';
  const medium = size === 'md';
  const large = size === 'lg';

  const withImage = !!image;
  const withOverlay = variant === 'overlay';
  const horizontal = size === 'lg' || withOverlay;
  const vertical = size !== 'lg' && !withOverlay;

  const badgeSale = badgeType === 'sale';
  const blackweek =
    badgeType === 'blackweek' ||
    badgeType === 'blackweek-logo-light' ||
    badgeType === 'blackweek-logo-dark';
  const blackweekLogoLight = badgeType === 'blackweek-logo-light';
  const blackweekLogoDark = badgeType === 'blackweek-logo-dark';
  const badgeNews = badgeType === 'news';
  const badgePromotion = badgeType === 'promotion';
  const badgeSaleRed = badgeType === 'sale-red';
  const badgeSalgLogo = badgeType === 'salg-logo';
  const customBrandingLogo = badgeType === 'customBrandingLogo';
  const saleWIthExtraBubble = badgeType === 'sale-with-extra-bubble';
  const withBadge =
    (badgeSale && badgeText) ||
    badgeNews ||
    badgePromotion ||
    (badgeText && blackweek) ||
    blackweekLogoLight ||
    blackweekLogoDark ||
    badgeSaleRed ||
    badgeSalgLogo ||
    customBrandingLogo ||
    saleWIthExtraBubble;

  const blockWithImage = (
    <div
      className={clsx(
        'flex',
        'group',
        'relative',
        'h-full',
        'w-full',
        '@container',
        {
          'flex-col': vertical,
          'flex-row': horizontal,
          'h-full': size !== 'lg',
          // 'w-full': !fill,
          // 'min-h-max': !fill,
          // 'max-w-full': !fill,
          // 'md:min-h-[300px]': !fill,
          // 'md:aspect-[128/50]': withOverlay,
          // 'flex-col': !large,
          // 'max-w-[680px]': medium,
          // 'max-w-[480px]': small,
          // 'flex-row-reverse': imagePosition === 'after',
          // 'w-full h-full': fill,
          'text-neutral-0': withOverlay,
          'bg-neutral-80 text-neutral-0':
            theme === 'neutralDark' && !withOverlay,
          'bg-neutral-40 text-neutral-80': theme === 'neutral' && !withOverlay,
          'bg-green-40 text-neutral-80': theme === 'lightGreen' && !withOverlay,
          'bg-brown-30 text-neutral-80': theme === 'lightBrown' && !withOverlay,
          'bg-tetiery-30 text-neutral-80':
            theme === 'lightYellow' && !withOverlay,
        },
        className
      )}
    >
      <div
        className={clsx('overflow-hidden', 'relative', {
          'w-full': withOverlay,
          'md:w-[70%]': size === 'lg' && !withOverlay,
          'aspect-[16/9]': horizontal,
          // 'md:aspect-[16/9]': horizontal && !withOverlay,
          'aspect-[16/11]': vertical,
          'h-full absolute': withOverlay,
        })}
      >
        <figure
          className={clsx(
            'w-full',
            'h-full',
            'object-cover-child-img',
            'bg-neutral-60',
            {
              'overlay-after': withOverlay,
              absolute: withOverlay,
            }
          )}
        >
          {typeof image === 'string' ? (
            <img
              src={image}
              alt="Preview image"
              style={{ width: '100%', height: '100%' }}
            />
          ) : (
            image
          )}
        </figure>
        {withBadge && (
          <CampaignCardBadge
            badgeNews={badgeNews}
            badgePreText={badgePreText}
            badgePromotion={badgePromotion}
            badgeSale={badgeSale}
            blackweek={blackweek}
            blackweekLogoLight={blackweekLogoLight}
            blackweekLogoDark={blackweekLogoDark}
            badgeSaleRed={badgeSaleRed}
            badgeSalgLogo={badgeSalgLogo}
            badgeText={badgeText}
            customBrandingLogo={customBrandingLogo}
            topLeftBranding={topLeftBranding}
            saleWithExtraBubble={saleWIthExtraBubble}
            smallBadgeText={smallBadgeText}
            large={large}
            medium={medium}
            small={small}
            withImage={withImage}
            parentType="v1"
          />
        )}
      </div>

      {theme === 'salgLogo' && (
        <div
          className="absolute h-1/2 lg:h-[65%] w-auto aspect-square top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            backgroundImage: 'url(/gfx/salgsalgsalg.svg)',
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            backgroundColor: 'transparent',
          }}
        ></div>
      )}

      {theme === 'customBrandingLogo' && (
        <div
          className="absolute h-1/2 w-auto aspect-square top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            backgroundImage: `url(${centerBranding.croppedImageUrl})`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            backgroundColor: 'transparent',
          }}
        ></div>
      )}

      {includeTopBranding && topBranding && (
        <div
          className="absolute flex flex-col justify-center items-center top-4 right-4 w-20 @lg:w-40 aspect-video"
          style={{
            backgroundImage: `url(${topBranding.croppedImageUrl})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundColor: 'transparent',
            backgroundPosition: 'top right',
          }}
        ></div>
      )}

      <div
        className={clsx('flex items-start', 'min-h-max', {
          'absolute bottom-0 left-0 w-full h-full': withOverlay,
          relative: horizontal && !withOverlay,
          'flex-col': !withOverlay,
          // 'justify-between': !withOverlay,
          'justify-between items-end': withOverlay,
          'justify-end': (small || medium) && withOverlay,
          'w-full': !(large && !withOverlay),
          'md:w-[33.5%]': large && !withOverlay,
          'p-4': true,
          'p-6': medium || (large && !withOverlay),
          'md:p-12': large && withOverlay,
        })}
      >
        <header>
          {/* {subTitle && <p className="body-sm">{subTitle}</p>} */}
          {title && (
            <h3
              className={clsx('group-hover:underline', {
                'hd-md': small,
                'hd-lg': medium,
                'md:hd-xl': large,
              })}
            >
              {processTitle(title)}
            </h3>
          )}
        </header>
        {body && large && !withOverlay && (
          <div
            className={clsx('body-sm', 'mb-4', 'leading-loose', {
              'mt-4': !withOverlay,
              'lg:body-base': large,
            })}
          >
            {body}
          </div>
        )}
        {showButton && (
          <IconButton
            className={clsx({
              'invisible md:visible': horizontal && !withOverlay,
            })}
            variant={theme === 'neutralDark' ? 'ghost' : 'primary'}
            size="md"
            title={`Gå til ${title}`}
          >
            <ArrowRightIcon className="w-7 h-7" />
          </IconButton>
        )}
      </div>
    </div>
  );

  const blockWithoutImage = (
    <div
      className={clsx(
        'flex',
        'flex-col',
        'md:flex-row',
        'relative',
        'w-full',
        'min-h-max',
        'max-w-full',
        {
          'flex-col': !large,
          'h-[144px]': large,
          'h-[350px]': small || medium,
          'max-w-[640px]': medium,
          'max-w-[480px]': small,
          'text-neutral-0': withOverlay,
          'bg-neutral-80 text-neutral-0':
            theme === 'neutralDark' && !withOverlay,
          'bg-neutral-40 text-neutral-80': theme === 'neutral' && !withOverlay,
          'bg-green-40 text-neutral-80': theme === 'lightGreen' && !withOverlay,
          'bg-brown-30 text-neutral-80': theme === 'lightBrown' && !withOverlay,
          'bg-tetiery-30 text-neutral-80':
            theme === 'lightYellow' && !withOverlay,
        },
        className
      )}
    >
      <div className={clsx('overflow-hidden')}>
        {withBadge && (
          <CampaignCardBadge
            badgeNews={badgeNews}
            badgePreText={badgePreText}
            badgePromotion={badgePromotion}
            badgeSale={badgeSale}
            blackweek={blackweek}
            blackweekLogoLight={blackweekLogoLight}
            blackweekLogoDark={blackweekLogoDark}
            badgeSaleRed={badgeSaleRed}
            badgeSalgLogo={badgeSalgLogo}
            badgeText={badgeText}
            large={large}
            medium={medium}
            small={small}
            withImage={withImage}
            parentType="v1"
          />
        )}
      </div>
      <div
        className={clsx(
          'flex items-start',
          'min-h-max',
          'justify-between',
          'p-4',
          {
            'flex-col': !large,
            'items-center': large,
            'w-full': !large,
            'p-6': medium,
            'md:pl-32 pr-6 py-6': large,
          }
        )}
      >
        <div className={clsx('')}>
          <header>
            {title && (
              <h3
                className={clsx({
                  'hd-md': small,
                  'hd-lg': medium,
                  'md:hd-xl': large,
                })}
              >
                {title}
              </h3>
            )}
          </header>
          {body && (
            <div className={clsx('body-sm', 'mb-4', 'leading-loose', 'mt-4')}>
              {body}
            </div>
          )}
        </div>

        <IconButton variant="ghost" size="lg" title={`Gå til ${title}`}>
          <ArrowRightIcon className="w-5 h-5" />
        </IconButton>
      </div>
    </div>
  );

  return withImage ? blockWithImage : blockWithoutImage;
});

export default CampaignCard;
